(() => {
  const productSearchCache = new Map();
  const productSearchLimit = 8;
  const productSearchMinimumCharacters = 2;
  const productSearchDebounce = 220;

  const resetProductSummary = (root) => {
    const summary = root.querySelector('[data-quote-product-name]');
    const image = root.querySelector('[data-quote-product-image]');
    const fallback = root.querySelector('[data-quote-product-fallback]');
    const status = root.querySelector('[data-quote-product-status]');

    if (summary) summary.textContent = root.dataset.emptyProduct || '';
    if (image) {
      image.setAttribute('hidden', '');
      image.removeAttribute('src');
    }
    if (fallback) fallback.removeAttribute('hidden');
    if (status) status.setAttribute('hidden', '');
  };

  const updateProductSummary = (root, product) => {
    const title = typeof product?.title === 'string' ? product.title.trim() : '';
    if (!title) {
      resetProductSummary(root);
      return;
    }

    const summary = root.querySelector('[data-quote-product-name]');
    const image = root.querySelector('[data-quote-product-image]');
    const fallback = root.querySelector('[data-quote-product-fallback]');
    const status = root.querySelector('[data-quote-product-status]');
    const imageUrl = typeof product.imageUrl === 'string' ? product.imageUrl.trim() : '';

    if (summary) summary.textContent = title;
    if (image && imageUrl) {
      image.src = imageUrl;
      image.removeAttribute('hidden');
      if (fallback) fallback.setAttribute('hidden', '');
    } else {
      if (image) {
        image.setAttribute('hidden', '');
        image.removeAttribute('src');
      }
      if (fallback) fallback.removeAttribute('hidden');
    }
    if (status) {
      if (product.isCatalogProduct === true) {
        status.removeAttribute('hidden');
      } else {
        status.setAttribute('hidden', '');
      }
    }
  };

  const normalizeProductQuery = (value) =>
    Array.from(value.trim().replace(/\s+/g, ' ')).slice(0, 100).join('');

  const createProductAutocomplete = (root, input) => {
    const combobox = root.querySelector('[data-quote-product-combobox]');
    const results = root.querySelector('[data-quote-product-results]');
    const status = root.querySelector('[data-quote-product-search-status]');
    const searchUrl = root.dataset.productSearchUrl;
    const titleMapElement = root.querySelector('[data-quote-product-title-map]');

    if (!combobox || !results || !status || !searchUrl) return null;

    let safeProductTitles = {};
    if (titleMapElement) {
      try {
        const parsedTitleMap = JSON.parse(titleMapElement.textContent);
        if (parsedTitleMap && typeof parsedTitleMap === 'object') safeProductTitles = parsedTitleMap;
      } catch {
        safeProductTitles = {};
      }
    }

    let activeIndex = -1;
    let abortController = null;
    let currentQuery = '';
    let debounceTimer = null;
    let destroyed = false;
    let requestSequence = 0;

    const getOptions = () =>
      Array.from(results.querySelectorAll('[data-quote-product-option]'));

    const getQuery = () => normalizeProductQuery(input.value);

    const hasMinimumCharacters = (query) =>
      query.replace(/\s/g, '').length >= productSearchMinimumCharacters;

    const cacheKey = (query) =>
      `${document.documentElement.lang || 'en'}:${query.toLocaleLowerCase()}`;

    const setStatus = (message = '') => {
      status.textContent = message;
    };

    const clearSelection = () => {
      getOptions().forEach((option) => option.setAttribute('aria-selected', 'false'));
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
    };

    const close = ({ clearStatus = false } = {}) => {
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      clearSelection();
      if (clearStatus) setStatus();
    };

    const open = () => {
      if (getOptions().length === 0) return;
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    const cancelPendingSearch = () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = null;
      requestSequence += 1;
      abortController?.abort();
      abortController = null;
      results.setAttribute('aria-busy', 'false');
    };

    const buildOptions = (products) => {
      const fragment = document.createDocumentFragment();

      products.forEach((product, index) => {
        const option = document.createElement('li');
        const title = document.createElement('span');
        option.className = 'quote-request__product-option';
        option.id = `${results.id}-option-${index + 1}`;
        option.dataset.productTitle = product.title;
        option.dataset.productImageUrl = product.imageUrl;
        option.dataset.quoteProductOption = '';
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');

        if (product.imageUrl) {
          const image = document.createElement('img');
          image.className = 'quote-request__product-option-image';
          image.src = product.imageUrl;
          image.alt = '';
          image.loading = 'lazy';
          image.decoding = 'async';
          option.append(image);
        }

        title.className = 'quote-request__product-option-title';
        title.textContent = product.title;
        option.append(title);
        fragment.append(option);
      });

      results.replaceChildren(fragment);
    };

    const parseResults = (responseData) => {
      const products = responseData?.resources?.results?.products;
      if (!Array.isArray(products)) throw new Error('Invalid predictive search response');

      const seenTitles = new Set();
      const uniqueProducts = [];

      products.forEach((product) => {
        const sourceTitle = typeof product?.title === 'string' ? product.title.trim() : '';
        const productUrl = typeof product?.url === 'string' ? product.url.trim() : '';
        let productHandle = '';
        if (productUrl) {
          const encodedHandle = productUrl.split('?')[0].split('/').filter(Boolean).pop() || '';
          try {
            productHandle = decodeURIComponent(encodedHandle);
          } catch {
            productHandle = encodedHandle;
          }
        }
        const title = safeProductTitles[productHandle] || '';
        const normalizedTitle = title.toLocaleLowerCase();
        const featuredImageUrl =
          typeof product?.featured_image?.url === 'string'
            ? product.featured_image.url.trim()
            : '';
        const imageUrl =
          featuredImageUrl || (typeof product?.image === 'string' ? product.image.trim() : '');

        if (!title || seenTitles.has(normalizedTitle)) return;
        seenTitles.add(normalizedTitle);
        uniqueProducts.push({ imageUrl, isCatalogProduct: true, sourceTitle, title });
      });

      return {
        status:
          uniqueProducts.length > 0
            ? root.dataset.productSearchResults || ''
            : root.dataset.productSearchEmpty || '',
        products: uniqueProducts.slice(0, productSearchLimit),
      };
    };

    const fetchResults = async (query, signal) => {
      const key = cacheKey(query);
      if (productSearchCache.has(key)) return productSearchCache.get(key);

      const url = new URL(searchUrl, window.location.origin);
      url.pathname = `${url.pathname.replace(/\/$/, '')}.json`;
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', String(productSearchLimit));
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set(
        'resources[options][fields]',
        'title,product_type,variants.title,variants.sku,vendor'
      );
      url.searchParams.set('resources[options][unavailable_products]', 'last');

      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        signal,
      });

      if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);

      const result = parseResults(await response.json());
      productSearchCache.set(key, result);
      return result;
    };

    const renderResults = (result, query) => {
      buildOptions(result.products);
      currentQuery = query;
      setStatus(result.status);

      if (result.products.length > 0) {
        open();
      } else {
        close();
      }
    };

    const search = async (query) => {
      const sequence = ++requestSequence;
      abortController?.abort();
      const controller = new AbortController();
      abortController = controller;
      results.setAttribute('aria-busy', 'true');
      setStatus(root.dataset.productSearchLoading || '');

      try {
        const result = await fetchResults(query, controller.signal);

        if (destroyed || sequence !== requestSequence || query !== getQuery()) return;
        renderResults(result, query);
      } catch (error) {
        if (error.name === 'AbortError' || destroyed || sequence !== requestSequence) return;

        results.replaceChildren();
        currentQuery = '';
        close();
        setStatus(
          [root.dataset.productSearchError, root.dataset.productSearchFallback]
            .filter(Boolean)
            .join('. ')
        );
      } finally {
        if (sequence === requestSequence) {
          results.setAttribute('aria-busy', 'false');
          if (abortController === controller) abortController = null;
        }
      }
    };

    const scheduleSearch = (query, delay = productSearchDebounce) => {
      cancelPendingSearch();

      if (!hasMinimumCharacters(query)) {
        results.replaceChildren();
        currentQuery = '';
        close({ clearStatus: true });
        return;
      }

      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        search(query);
      }, delay);
    };

    const moveSelection = (direction) => {
      const options = getOptions();
      if (options.length === 0) return;

      if (activeIndex < 0) {
        activeIndex = direction > 0 ? 0 : options.length - 1;
      } else {
        activeIndex = (activeIndex + direction + options.length) % options.length;
      }

      options.forEach((option, index) => {
        option.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false');
      });
      input.setAttribute('aria-activedescendant', options[activeIndex].id);
      options[activeIndex].scrollIntoView({ block: 'nearest' });
    };

    const productFromOption = (option) => ({
      imageUrl: option?.dataset.productImageUrl || '',
      isCatalogProduct: true,
      title: option?.dataset.productTitle || option?.textContent.trim() || '',
    });

    const setValue = (product) => {
      const normalizedProduct =
        typeof product === 'string'
          ? { imageUrl: '', isCatalogProduct: false, title: product }
          : product;
      const title =
        typeof normalizedProduct?.title === 'string' ? normalizedProduct.title.trim() : '';

      cancelPendingSearch();
      input.value = title;
      currentQuery = '';
      results.replaceChildren();
      close({ clearStatus: true });
      updateProductSummary(root, normalizedProduct);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const resolveExactTitle = async (value) => {
      const query = normalizeProductQuery(value);
      if (!hasMinimumCharacters(query)) return false;

      const controller = new AbortController();

      try {
        const result = await fetchResults(query, controller.signal);
        if (destroyed || query !== getQuery()) return false;

        const normalizedTitle = query.toLocaleLowerCase();
        const exactProduct = result.products.find(
          (product) =>
            product.title.toLocaleLowerCase() === normalizedTitle ||
            product.sourceTitle?.toLocaleLowerCase() === normalizedTitle
        );

        if (!exactProduct) return false;
        setValue(exactProduct);
        return true;
      } catch {
        return false;
      }
    };

    const onInput = () => {
      const query = getQuery();
      resetProductSummary(root);
      results.replaceChildren();
      currentQuery = '';
      close({ clearStatus: true });
      scheduleSearch(query);
    };

    const onFocus = () => {
      const query = getQuery();
      if (!hasMinimumCharacters(query)) return;

      if (query === currentQuery && getOptions().length > 0) {
        open();
        return;
      }

      const cachedResult = productSearchCache.get(cacheKey(query));
      if (cachedResult) {
        renderResults(cachedResult, query);
        return;
      }

      scheduleSearch(query, 0);
    };

    const onKeydown = (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (getOptions().length === 0) return;
        event.preventDefault();
        open();
        moveSelection(event.key === 'ArrowDown' ? 1 : -1);
        return;
      }

      if (event.key === 'Enter' && activeIndex >= 0 && !results.hidden) {
        const selectedOption = getOptions()[activeIndex];
        if (!selectedOption) return;
        event.preventDefault();
        setValue(productFromOption(selectedOption));
        return;
      }

      if (
        event.key === 'Escape' &&
        (!results.hidden || abortController || debounceTimer !== null)
      ) {
        event.preventDefault();
        cancelPendingSearch();
        close({ clearStatus: true });
        return;
      }

      if (event.key === 'Tab') {
        cancelPendingSearch();
        close({ clearStatus: true });
      }
    };

    const onResultClick = (event) => {
      const option = event.target.closest('[data-quote-product-option]');
      if (!option || !results.contains(option)) return;

      event.preventDefault();
      input.focus();
      setValue(productFromOption(option));
    };

    const onFocusout = () => {
      window.setTimeout(() => {
        if (!combobox.contains(document.activeElement)) {
          cancelPendingSearch();
          close({ clearStatus: true });
          void resolveExactTitle(input.value);
        }
      });
    };

    const onDocumentPointerdown = (event) => {
      if (!combobox.contains(event.target)) {
        cancelPendingSearch();
        close({ clearStatus: true });
      }
    };

    input.addEventListener('input', onInput);
    input.addEventListener('focus', onFocus);
    input.addEventListener('keydown', onKeydown);
    results.addEventListener('click', onResultClick);
    combobox.addEventListener('focusout', onFocusout);
    document.addEventListener('pointerdown', onDocumentPointerdown);

    return {
      destroy() {
        destroyed = true;
        cancelPendingSearch();
        input.removeEventListener('input', onInput);
        input.removeEventListener('focus', onFocus);
        input.removeEventListener('keydown', onKeydown);
        results.removeEventListener('click', onResultClick);
        combobox.removeEventListener('focusout', onFocusout);
        document.removeEventListener('pointerdown', onDocumentPointerdown);
        close({ clearStatus: true });
      },
      resolveExactTitle,
      setValue,
    };
  };

  const removeQuoteSuccessMarker = () => {
    const successUrl = new URL(window.location.href);
    if (!successUrl.searchParams.has('contact_posted')) return;
    successUrl.searchParams.delete('contact_posted');
    window.history.replaceState(window.history.state, '', successUrl.toString());
  };

  const scheduleQuoteSuccessMarkerCleanup = () => {
    const scheduleCleanup = () => window.setTimeout(removeQuoteSuccessMarker, 5000);
    if (document.readyState === 'complete') {
      scheduleCleanup();
      return;
    }
    window.addEventListener('load', scheduleCleanup, { once: true });
  };

  const quoteAttributionInputs = (root) => ({
    quoteId: root.querySelector('[data-jisc-quote-id]'),
    gclid: root.querySelector('[data-jisc-gclid]'),
    gbraid: root.querySelector('[data-jisc-gbraid]'),
    wbraid: root.querySelector('[data-jisc-wbraid]'),
    capturedAt: root.querySelector('[data-jisc-click-captured-at]'),
    adUserData: root.querySelector('[data-jisc-ad-user-data]'),
    adPersonalization: root.querySelector('[data-jisc-ad-personalization]'),
  });

  const ensureQuoteTrackingId = (root) => {
    const { quoteId } = quoteAttributionInputs(root);
    if (!quoteId) return '';

    if (!root.dataset.jiscQuoteTrackingId && typeof window.crypto?.randomUUID === 'function') {
      root.dataset.jiscQuoteTrackingId = window.crypto.randomUUID();
    }

    quoteId.value = root.dataset.jiscQuoteTrackingId || '';
    return quoteId.value;
  };

  const clearQuoteAttributionFields = (root) => {
    const inputs = quoteAttributionInputs(root);
    [
      inputs.gclid,
      inputs.gbraid,
      inputs.wbraid,
      inputs.capturedAt,
      inputs.adUserData,
      inputs.adPersonalization,
    ].forEach((input) => {
      if (input) input.value = '';
    });
  };

  const hydrateQuoteAttributionFields = (root) => {
    clearQuoteAttributionFields(root);

    let attribution = null;
    try {
      attribution = window.JiscGoogleClickAttribution?.refresh?.() || null;
    } catch {
      attribution = null;
    }

    if (!attribution) return false;

    const inputs = quoteAttributionInputs(root);
    if (inputs.gclid) inputs.gclid.value = attribution.identifiers?.gclid || '';
    if (inputs.gbraid) inputs.gbraid.value = attribution.identifiers?.gbraid || '';
    if (inputs.wbraid) inputs.wbraid.value = attribution.identifiers?.wbraid || '';
    if (inputs.capturedAt) inputs.capturedAt.value = attribution.capturedAt || '';
    if (inputs.adUserData) inputs.adUserData.value = attribution.adUserData || '';
    if (inputs.adPersonalization) {
      inputs.adPersonalization.value = attribution.adPersonalization || '';
    }

    return Boolean(
      inputs.gclid?.value || inputs.gbraid?.value || inputs.wbraid?.value
    );
  };

  const initializeQuoteRequest = (root) => {
    if (root.dataset.quoteRequestReady === 'true') return;
    root.dataset.quoteRequestReady = 'true';

    const cleanupFunctions = [];
    const productInput = root.querySelector('[data-quote-product-input]');
    const productImage = root.querySelector('[data-quote-product-image]');
    const productFallback = root.querySelector('[data-quote-product-fallback]');
    const unsureButton = root.querySelector('[data-quote-unsure]');
    const successfulRequest = root.querySelector('[data-quote-request-success]');
    const quoteForm = root.querySelector('form.quote-request__form');
    const scanner = new URL(window.location.href).searchParams.get('scanner');
    const normalizedScanner = scanner
      ? Array.from(scanner.trim()).slice(0, 200).join('')
      : '';
    let productAutocomplete = null;

    resetProductSummary(root);
    ensureQuoteTrackingId(root);
    hydrateQuoteAttributionFields(root);

    const attributionApi = window.JiscGoogleClickAttribution;
    if (attributionApi?.ready && typeof attributionApi.ready.then === 'function') {
      void attributionApi.ready.then(() => {
        if (root.dataset.quoteRequestReady === 'true') {
          hydrateQuoteAttributionFields(root);
        }
      });
    }

    if (quoteForm) {
      const onQuoteFormSubmit = () => {
        ensureQuoteTrackingId(root);
        hydrateQuoteAttributionFields(root);
      };
      quoteForm.addEventListener('submit', onQuoteFormSubmit);
      cleanupFunctions.push(() => quoteForm.removeEventListener('submit', onQuoteFormSubmit));
    }

    const onVisitorConsentCollected = () => hydrateQuoteAttributionFields(root);
    document.addEventListener('visitorConsentCollected', onVisitorConsentCollected);
    cleanupFunctions.push(() =>
      document.removeEventListener('visitorConsentCollected', onVisitorConsentCollected)
    );

    if (productImage) {
      const onProductImageError = () => {
        productImage.setAttribute('hidden', '');
        productImage.removeAttribute('src');
        if (productFallback) productFallback.removeAttribute('hidden');
      };

      productImage.addEventListener('error', onProductImageError);
      cleanupFunctions.push(() =>
        productImage.removeEventListener('error', onProductImageError)
      );
    }

    if (productInput) {
      if (normalizedScanner && !productInput.value.trim()) {
        productInput.value = normalizedScanner;
      }

      productAutocomplete = createProductAutocomplete(root, productInput);
      if (productAutocomplete) {
        cleanupFunctions.push(() => productAutocomplete.destroy());

        const initialValue = productInput.value.trim();
        const unsureValue = unsureButton?.dataset.unsureValue || '';
        if (initialValue && initialValue === unsureValue) {
          updateProductSummary(root, {
            imageUrl: '',
            isCatalogProduct: false,
            title: initialValue,
          });
        } else if (initialValue) {
          void productAutocomplete.resolveExactTitle(initialValue);
        }
      } else {
        const onProductInput = () => resetProductSummary(root);
        productInput.addEventListener('input', onProductInput);
        cleanupFunctions.push(() => productInput.removeEventListener('input', onProductInput));
      }
    } else if (normalizedScanner) {
      updateProductSummary(root, {
        imageUrl: '',
        isCatalogProduct: false,
        title: normalizedScanner,
      });
    }

    if (productInput && unsureButton) {
      const onUnsureClick = () => {
        const unsureValue = unsureButton.dataset.unsureValue || '';
        productInput.focus();

        if (productAutocomplete) {
          productAutocomplete.setValue({
            imageUrl: '',
            isCatalogProduct: false,
            title: unsureValue,
          });
        } else {
          productInput.value = unsureValue;
          updateProductSummary(root, {
            imageUrl: '',
            isCatalogProduct: false,
            title: unsureValue,
          });
        }
      };

      unsureButton.addEventListener('click', onUnsureClick);
      cleanupFunctions.push(() => unsureButton.removeEventListener('click', onUnsureClick));
    }

    if (successfulRequest) {
      scheduleQuoteSuccessMarkerCleanup();
    }

    root.quoteRequestCleanup = () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
      cleanupFunctions.length = 0;
      delete root.quoteRequestCleanup;
      delete root.dataset.quoteRequestReady;
    };
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-quote-request]').forEach(initializeQuoteRequest);
  };

  initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-quote-request]').forEach((root) => {
      root.quoteRequestCleanup?.();
    });
  });
})();
